import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Flex,
  Text,
  Box,
  Select,
  Spinner,
  Input,
  InputGroup,
  InputLeftElement,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
} from '@chakra-ui/react';
import { ShieldCheck, MagnifyingGlass, Plus } from 'phosphor-react';
import { toast } from 'sonner';
import { PageBox } from '../../../../../components/pageBox';
import { ReturnButton } from '../../../../../components/buttons/ReturnButton';
import { useRegimesEspeciais } from '../../../../../hooks/useRegimesEspeciais';
import { RegimesEspeciaisKpis } from './components/RegimesEspeciaisKpis';
import { RegimesEspeciaisTable } from './components/RegimesEspeciaisTable';
import { RegimeEspecialFormModal } from './components/RegimeEspecialFormModal';
import { IRegimeEspecial } from '../../../../../types/regimeEspecial';

export function IcmsRegimeEspecialPage() {
  const queryClient = useQueryClient();
  const { getRegimesEspeciaisCarteira, getRegimesEspeciaisKpis, deleteRegimeEspecial } =
    useRegimesEspeciais();

  const [searchParams, setSearchParams] = useSearchParams();
  const esfera = searchParams.get('esfera') || '';
  const status = searchParams.get('status') || '';
  const uf = searchParams.get('uf') || '';
  const search = searchParams.get('search') || '';
  const page = parseInt(searchParams.get('page') || '1', 10);
  const size = parseInt(searchParams.get('size') || '50', 10);

  const formModal = useDisclosure();
  const deleteDialog = useDisclosure();
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const [editing, setEditing] = useState<IRegimeEspecial | null>(null);
  const [deleting, setDeleting] = useState<IRegimeEspecial | null>(null);
  const [newEmpresaId, setNewEmpresaId] = useState<number | null>(null);

  const filters = useMemo(
    () => ({
      ...(esfera ? { esfera } : {}),
      ...(status ? { status } : {}),
      ...(uf ? { uf } : {}),
      ...(search ? { search } : {}),
      page,
      size,
    }),
    [esfera, status, uf, search, page, size],
  );

  const { data: kpisData, isLoading: isLoadingKpis } = useQuery({
    queryKey: ['regimes-especiais-kpis', esfera ? { esfera } : {}],
    queryFn: getRegimesEspeciaisKpis,
    staleTime: 5 * 60 * 1000,
  });

  const tableQuery = useQuery({
    queryKey: ['regimes-especiais', filters],
    queryFn: getRegimesEspeciaisCarteira,
    placeholderData: prev => prev,
  });

  const items = tableQuery.data?.data?.items ?? [];
  const pagination = {
    total: tableQuery.data?.data?.total ?? 0,
    pages: tableQuery.data?.data?.pages ?? 0,
    page: tableQuery.data?.data?.page ?? 1,
    size: tableQuery.data?.data?.size ?? size,
    next_page: tableQuery.data?.data?.next_page ?? null,
    previous_page: tableQuery.data?.data?.previous_page ?? null,
  };

  const setQueryParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    next.set('page', '1');
    setSearchParams(next, { replace: true });
  };

  const deleteMutation = useMutation({
    mutationFn: async (row: IRegimeEspecial) => deleteRegimeEspecial(row.empresa_id, row.id),
    onSuccess: () => {
      toast.success('Regime removido');
      queryClient.invalidateQueries({ queryKey: ['regimes-especiais'] });
      queryClient.invalidateQueries({ queryKey: ['regimes-especiais-kpis'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || 'Erro ao remover. Tente novamente.');
    },
  });

  return (
    <PageBox>
      <Flex align="center" pb={5} justify="space-between">
        <Flex align="center">
          <ReturnButton previousPage="/modulesHome/supervisor/icms" />
          <Box h="full">
            <Flex align="center" justify="center" borderRadius="full" p={3} bg="#E8F5E9">
              <ShieldCheck color="#4CAF50" className="size-8" />
            </Flex>
          </Box>
          <Flex direction="column" justify="center" gap={1} pl={2}>
            <Text fontWeight="bold" fontSize="16px">
              Regime Especial · Cofre de Benefícios
            </Text>
            <Text fontSize="12px" color="gray.500">
              Carteira consolidada de regimes especiais (federal, estadual, municipal).
            </Text>
          </Flex>
        </Flex>
        <Button
          size="sm"
          colorScheme="blue"
          leftIcon={<Plus size={16} />}
          onClick={() => {
            setEditing(null);
            setNewEmpresaId(null);
            formModal.onOpen();
          }}
        >
          Novo regime
        </Button>
      </Flex>

      <RegimesEspeciaisKpis data={kpisData} isLoading={isLoadingKpis} />

      <Flex gap={2} mb={3} wrap="wrap" align="center">
        <InputGroup size="sm" maxW="280px">
          <InputLeftElement pointerEvents="none">
            <MagnifyingGlass size={14} color="#94a3b8" />
          </InputLeftElement>
          <Input
            placeholder="Buscar empresa, ato ou emissor..."
            value={search}
            background="body"
            onChange={e => setQueryParam('search', e.target.value)}
          />
        </InputGroup>
        <Select
          size="sm"
          maxW="160px"
          background="body"
          value={esfera}
          onChange={e => setQueryParam('esfera', e.target.value)}
        >
          <option value="">Todas esferas</option>
          <option value="federal">Federal</option>
          <option value="estadual">Estadual</option>
          <option value="municipal">Municipal</option>
        </Select>
        <Select
          size="sm"
          maxW="160px"
          background="body"
          value={status}
          onChange={e => setQueryParam('status', e.target.value)}
        >
          <option value="">Todos status</option>
          <option value="ativo">Ativo</option>
          <option value="suspenso">Suspenso</option>
          <option value="encerrado">Encerrado</option>
        </Select>
        <Input
          size="sm"
          maxW="80px"
          placeholder="UF"
          value={uf}
          background="body"
          onChange={e => setQueryParam('uf', e.target.value.toUpperCase())}
        />
        {tableQuery.isFetching && <Spinner size="sm" color="primary" />}
      </Flex>

      <RegimesEspeciaisTable
        data={items}
        pagination={pagination}
        isFetching={tableQuery.isFetching}
        showEmpresa
        onEdit={row => {
          setEditing(row);
          formModal.onOpen();
        }}
        onDelete={row => {
          setDeleting(row);
          deleteDialog.onOpen();
        }}
      />

      {formModal.isOpen && (editing || newEmpresaId) && (
        <RegimeEspecialFormModal
          isOpen={formModal.isOpen}
          onClose={() => {
            formModal.onClose();
            setNewEmpresaId(null);
          }}
          empresaId={editing?.empresa_id ?? newEmpresaId!}
          regime={editing}
        />
      )}

      {formModal.isOpen && !editing && !newEmpresaId && (
        <Modal isOpen onClose={formModal.onClose} isCentered size="sm">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Novo regime especial</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              <Text fontSize="sm" color="gray.600" mb={2}>
                Informe o ID da empresa para cadastrar o regime:
              </Text>
              <Input
                placeholder="ID da empresa"
                type="number"
                autoFocus
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    const val = Number((e.target as HTMLInputElement).value);
                    if (val > 0) setNewEmpresaId(val);
                  }
                }}
                id="new-regime-empresa-input"
              />
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={formModal.onClose}>
                Cancelar
              </Button>
              <Button
                colorScheme="blue"
                onClick={() => {
                  const input = document.getElementById(
                    'new-regime-empresa-input',
                  ) as HTMLInputElement;
                  const val = Number(input?.value);
                  if (val > 0) setNewEmpresaId(val);
                }}
              >
                Continuar
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      <AlertDialog
        isOpen={deleteDialog.isOpen}
        leastDestructiveRef={cancelRef}
        onClose={deleteDialog.onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Remover regime especial
            </AlertDialogHeader>
            <AlertDialogBody>
              Confirmar remoção do regime <strong>{deleting?.tipo}</strong>
              {deleting?.numero_ato ? ` (ato ${deleting.numero_ato})` : ''}?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={deleteDialog.onClose}>
                Cancelar
              </Button>
              <Button
                colorScheme="red"
                ml={3}
                isLoading={deleteMutation.isPending}
                onClick={() => {
                  if (deleting) {
                    deleteMutation.mutate(deleting, {
                      onSuccess: () => deleteDialog.onClose(),
                    });
                  }
                }}
              >
                Remover
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </PageBox>
  );
}
